import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { DashboardShell } from "../components/DashboardShell";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC"];

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", title: user.title || "", phone: user.phone || "",
        timezone: user.timezone || "America/New_York", preferred_language: user.preferred_language || "en",
        bio: user.bio || "", notification_inapp: user.notification_inapp !== false,
        notification_email: user.notification_email !== false,
      });
    }
  }, [user]);

  if (!form) return <DashboardShell title="Profile"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { data: form });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Profile updated.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <DashboardShell title="My profile" subtitle={`${user?.email} · ${user?.role} — role, credentials, and account status are managed by your practice administrator.`}>
      <div className="card-warm p-8 max-w-2xl space-y-5" data-testid="profile-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2"><Label>Full name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Professional title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="profile-title" placeholder="e.g., Lead Assessor" className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Time zone</Label>
            <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger data-testid="profile-timezone" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Preferred language</Label>
            <Select value={form.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
              <SelectTrigger data-testid="profile-language" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem><SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem><SelectItem value="pt">Portuguese</SelectItem>
              </SelectContent>
            </Select></div>
        </div>
        <div className="space-y-2"><Label>Professional biography</Label>
          <Textarea rows={4} value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="profile-bio" className="input-warm border-[#E5E5DF] text-sm" /></div>
        <div className="rounded-xl border border-[#E5E5DF] p-5 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">Notification preferences</p>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Checkbox checked={form.notification_inapp} onCheckedChange={(v) => set("notification_inapp", !!v)} data-testid="profile-notify-inapp" />
            In-app notifications for new messages
          </label>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <Checkbox checked={form.notification_email} onCheckedChange={(v) => set("notification_email", !!v)} data-testid="profile-notify-email" />
            Email notifications (when email delivery is enabled)
          </label>
        </div>
        {user?.credentials && (
          <p className="text-xs text-[#5C5C57]">Credentials on file: <span className="font-semibold text-[#1C1C19]">{user.credentials}{user.license_state ? ` · ${user.license_state}` : ""}</span> (admin-managed)</p>
        )}
        <Button onClick={save} disabled={saving} data-testid="profile-save-btn"
          className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </DashboardShell>
  );
}
